"use client";

/**
 * The live "N elanı göstərin" count every phone filter sheet carries on
 * its bottom button (2026-10-29: one rule for every sheet): the list's
 * own total for a filter set (GET /api/listings, page_size=1), by the
 * grid's param rules (lib/listing-search-params: the sale default, "5+"
 * rooms, drawn areas), debounced, and remembered for a minute so moving
 * back and forth between choices asks once.
 */
import { useEffect, useState } from "react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { pluralForm } from "@/lib/announcements";
import type { Locale } from "@/lib/i18n-config";
import type { TranslationKey } from "@/lib/i18n";
import { apiParamsFromSearch } from "@/lib/listing-search-params";

export type ParamPatch = Record<string, string | string[] | null>;

const CACHE = new Map<string, { n: number; at: number }>();
const TTL_MS = 60_000;

/** A URL query with `patch` applied (null or [] clears a key). */
export function withPatch(base: URLSearchParams | ReadonlyURLSearchParams, patch: ParamPatch): URLSearchParams {
  const next = new URLSearchParams(base.toString());
  for (const [k, v] of Object.entries(patch)) {
    next.delete(k);
    if (Array.isArray(v)) v.forEach((x) => { if (x) next.append(k, x); });
    else if (v) next.set(k, v);
  }
  next.delete("page");
  return next;
}

function apiQuery(sp: URLSearchParams): string {
  const qp = new URLSearchParams();
  for (const [k, v] of Object.entries(apiParamsFromSearch(sp as unknown as ReadonlyURLSearchParams))) {
    if (k === "sort") continue;
    if (Array.isArray(v)) v.forEach((x) => { if (x) qp.append(k, x); });
    else if (v) qp.set(k, String(v));
  }
  qp.sort();
  qp.set("page_size", "1");
  return qp.toString();
}

/** The live total for a filter query (null while unknown or when off). */
export function useLiveCount(query: URLSearchParams | null, delayMs = 400): number | null {
  const key = query ? apiQuery(query) : null;
  const [n, setN] = useState<number | null>(() => (key ? CACHE.get(key)?.n ?? null : null));
  useEffect(() => {
    if (!key) { setN(null); return; }
    const hit = CACHE.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) { setN(hit.n); return; }
    const ctrl = new AbortController();
    const tid = setTimeout(() => {
      fetch(`/api/listings?${key}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (ctrl.signal.aborted || typeof d?.total !== "number") return;
          CACHE.set(key, { n: d.total, at: Date.now() });
          setN(d.total);
        })
        .catch(() => {});
    }, delayMs);
    return () => { clearTimeout(tid); ctrl.abort(); };
  }, [key, delayMs]);
  return n;
}

// Literal keys, so the per-route i18n scan finds them.
const SHOW_N = {
  one: "listings.filter.show_n.one",
  few: "listings.filter.show_n.few",
  many: "listings.filter.show_n.many",
} as const satisfies Record<string, TranslationKey>;

/** "1 243 elanı göstərin", or "Elanları göstərin" while the count is unknown. */
export function showLabel(t: (k: TranslationKey) => string, locale: Locale | string, n: number | null): string {
  if (n == null) return t("listings.filter.show");
  const fmt = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return t(SHOW_N[pluralForm(n, locale as Locale)]).replace("{n}", fmt);
}
